import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CopyrightRoundedIcon from "@mui/icons-material/CopyrightRounded";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ShareIcon from "@mui/icons-material/Share";
import Shop2OutlinedIcon from "@mui/icons-material/Shop2Outlined";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SupportIcon from "@mui/icons-material/Support";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import {
  Box,
  Button,
  Collapse,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SxProps,
  Tooltip,
  TypographyTypeMap,
} from "@mui/material";
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router";
import { cacheService } from "../../Core/Api/services";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import { ExternalLinks } from "../../Core/Api/Types/external-links";
import { Licences } from "../../Core/Api/Types/licences";
import { PlatformMode } from "../../Core/Api/Types/platform-mode";
import { routePaths } from "../../Router/router";
import { AclMatch, UserHasAcl, UserHasAcls, UserHasLicence } from "../../Utils/helpers";
import { settingsGroupsDetails } from "../../Pages/admin/Settings/settings";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { GetSectionExpanded, ToggleSection } from "../../Redux/Reducers/redux-slice-settings-users";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../Redux/redux-store";
import { useTranslation } from "react-i18next";
import { CloudUploadOutlined, HomeOutlined, MonetizationOnOutlined } from "@mui/icons-material";
import { useSelector } from "react-redux";

const isMatching = (pathName: string, paths: string[]): boolean => {
  return paths.some((path) => pathName.startsWith(path));
};

const getButtonSxProps: (open: boolean, expanded?: boolean) => SxProps = (open: boolean, expanded?: boolean) => ({
  pl: open ? "8px !important" : "6px",
  mr: open ? "12px" : 0,
  ml: open ? "12px" : 0,
  width: open ? "calc(100% - 24px)" : "32px",
  display: "flex !important",
  justifyContent: "center !important",
  background: expanded ? "#c2c2d61a" : undefined,
});

const STACK_BASE_PROPS: SxProps = {
  paddingTop: 1.5,
  paddingBottom: 1.5,
  marginLeft: 0,
  alignContent: "center",
  alignItems: "center",
  gap: "2px",
  width: "100%",
};
const STACK_BORDER_PROPS: SxProps = {
  borderBottom: "1px solid",
  borderColor: "divider",
};
const getStackSxProps: (withDivider: boolean) => SxProps = (withDivider: boolean) =>
  withDivider ? { ...STACK_BASE_PROPS, ...STACK_BORDER_PROPS } : STACK_BASE_PROPS;

type LeftMenuProps = {
  open: boolean;
};
type MenuSubItemInfos = {
  label: string;
  path: string;
  condition: boolean;
  matchingPaths: string[];
  newTab?: boolean;
};
type MenuItemInfos = MenuSubItemInfos & {
  icon: any;
  subItems: MenuSubItemInfos[] | null;
};

type MenuGroupInfos = { divider: boolean; items: MenuItemInfos[] };

const LeftMenu: React.FC<LeftMenuProps> = ({ open }) => {
  const { t } = useTranslation();
  const user = cacheService.GetUserInfo();
  const isCustomer = Boolean(user && user.customerIds && user.customerIds.length > 0);
  const platformMode: PlatformMode = cacheService.GetPlatformMode();
  const externalLinks: ExternalLinks[] = cacheService.GetAuthLoginResponse()?.externalLinks || [];
  const billingEnabled: boolean = cacheService.GetPlatformSettings()?.billingOfferId !== null;
  const currentSettingsGroup = settingsGroupsDetails();

  const generatedSettingsSubItems = useMemo(() => {
    return Object.keys(currentSettingsGroup).map((key) => ({
      label: key.charAt(0).toUpperCase() + key.slice(1).toLowerCase(),
      path: currentSettingsGroup[key].find((x) => UserHasAcls([...x.acls], AclMatch.OneAtLeast))?.path || "",
      matchingPaths: [`/settings/${key}/`],
      condition: currentSettingsGroup[key].some((x) => UserHasAcls([...x.acls], AclMatch.OneAtLeast)),
    }));
  }, [routePaths, currentSettingsGroup, user, user?.id]);

  const menuArray: MenuGroupInfos[] = user?.isExternalAccessSpecific
    ? []
    : [
        {
          divider: false,
          items: [
            {
              label: t("route:pageTitle.home"),
              path: routePaths.home.path,
              condition: platformMode !== PlatformMode.Browser,
              icon: <HomeOutlined />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("route:pageTitle.drive"),
              path: routePaths.drive.path,
              condition: platformMode === PlatformMode.Browser,
              icon: <HomeOutlined />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("route:pageTitle.drive"),
              path: routePaths.drive.path,
              condition:
                UserHasLicence(Licences.Drive) &&
                UserHasAcl(AclTypes.aclDrive) &&
                platformMode === PlatformMode.Distribution,
              icon: <CloudUploadOutlined />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("route:pageTitle.dashboard"),
              path: routePaths.dashboard.path,
              condition: true,
              icon: <DashboardOutlinedIcon />,
              subItems: null,
              matchingPaths: [],
            },
          ],
        },
        {
          divider: true,
          items: [
            {
              label: t("route:pageTitle.catalog"),
              path: routePaths.catalog.path,
              condition:
                isCustomer &&
                !UserHasAcl(AclTypes.aclLockAssetObjectAccess) &&
                UserHasAcl(AclTypes.aclSectionSearchAccess),
              icon: <SlideshowIcon />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("route:pageTitle.inventory"),
              path: routePaths.inventory.path,
              condition:
                !isCustomer &&
                platformMode === PlatformMode.Distribution &&
                !UserHasAcl(AclTypes.aclLockAssetObjectAccess) &&
                UserHasAcl(AclTypes.aclSectionSearchAccess),
              icon: <Inventory2OutlinedIcon />,
              subItems: null,
              matchingPaths: [routePaths.title.path, routePaths.asset.path, routePaths.catalog.path],
            },
            {
              label: t("route:linkTitle.sources"),
              path: `${routePaths.inventorySourceAdam.path}`,
              condition:
                !isCustomer &&
                platformMode === PlatformMode.Adam &&
                !UserHasAcl(AclTypes.aclLockAssetObjectAccess) &&
                UserHasAcl(AclTypes.aclSectionSearchAccess),
              icon: <Inventory2OutlinedIcon />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: "Livraisons",
              path: `${routePaths.inventoryAssets.path}`,
              condition:
                !isCustomer &&
                platformMode === PlatformMode.Adam &&
                !UserHasAcl(AclTypes.aclLockAssetObjectAccess) &&
                UserHasAcl(AclTypes.aclSectionSearchAccess),
              icon: <Inventory2OutlinedIcon />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("common:ingest"),
              path: routePaths.ingest.path,
              condition: UserHasAcls(
                [
                  AclTypes.aclIngestConfigCreation,
                  AclTypes.aclIngestPlaceholderCreate,
                  AclTypes.aclIngestWishListView,
                  AclTypes.aclIngestWishListViewAll,
                  AclTypes.aclIngestMonitor,
                  AclTypes.aclIngestRejectionsView,
                  AclTypes.aclSectionReportingAccess,
                ],
                AclMatch.OneAtLeast
              ),
              icon: <TaskOutlinedIcon />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("common:orders"),
              path: routePaths.ordersItemDetails.path,
              condition: UserHasAcl(AclTypes.aclSectionOrderAccess),
              icon: <Shop2OutlinedIcon />,
              subItems: null,
              matchingPaths: [routePaths.ordersBase.path, routePaths.ordersOverview.path],
            },
            {
              label: t("common:shares"),
              path: routePaths.shares.path,
              condition: UserHasAcl(AclTypes.aclShareView) && UserHasLicence(Licences.ShareScreening),
              icon: <ShareIcon />,
              subItems: null,
              matchingPaths: [],
            },
          ],
        },
        {
          divider: true,
          items: [
            {
              label: t("common:customers"),
              path: routePaths.customers.path,
              condition:
                platformMode === PlatformMode.Distribution &&
                UserHasAcl(AclTypes.aclSectionBackofficeAccess) &&
                UserHasLicence(Licences.CustomersManagement),
              icon: <GroupOutlinedIcon />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("route:pageTitle.providers"),
              path: routePaths.providers.path,
              condition: UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclBackofficeLabProviderCreate]),
              icon: <ManageAccountsOutlinedIcon />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("route:pageTitle.contracts"),
              path: routePaths.contracts.path,
              condition: platformMode === PlatformMode.Distribution && UserHasAcl(AclTypes.aclRightsView),
              icon: <CopyrightRoundedIcon />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("common:billing"),
              path: routePaths.billing.path,
              condition: billingEnabled && UserHasAcl(AclTypes.aclBillingAccess),
              icon: <MonetizationOnOutlined />,
              subItems: null,
              matchingPaths: [routePaths.billing.path, routePaths.billingInvoices.path],
            },
          ],
        },
        {
          divider: true,
          items: [
            {
              label: t("common:collections"),
              path: routePaths.collections.path,
              condition: !UserHasAcl(AclTypes.aclLockAssetObjectAccess) && platformMode !== PlatformMode.Adam,
              icon: <BookmarkBorderIcon />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("common:storefront"),
              path: routePaths.storefront.path,
              condition:
                platformMode === PlatformMode.Distribution &&
                UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclCollectionStorefrontAdmin]),
              icon: <StorefrontOutlinedIcon />,
              subItems: null,
              matchingPaths: [],
            },
          ],
        },
        {
          divider: true,
          items: [
            {
              label: t("common:reporting"),
              path: routePaths.reporting.path,
              condition: UserHasAcls([
                AclTypes.aclSuperUser,
                AclTypes.aclMetricView,
                AclTypes.aclSectionReportingAccess,
              ]),
              icon: <QueryStatsOutlinedIcon />,
              subItems: null,
              matchingPaths: [routePaths.reportingDataExporter.path],
            },
            {
              label: t("common:planning"),
              path: routePaths.planning.path,
              condition:
                UserHasAcl(AclTypes.aclPlanningUserTaskView) &&
                (UserHasLicence(Licences.PlanningUserTaskKanban) || UserHasLicence(Licences.PlanningUserTaskCalendar)),
              icon: <CalendarMonthIcon />,
              subItems: null,
              matchingPaths: [routePaths.planningKanban.path],
            },
            {
              label: t("common:support"),
              path: UserHasAcl(AclTypes.aclDocumentsAccess)
                ? routePaths.supportDocuments.path
                : routePaths.supportTickets.path,
              condition: UserHasAcls([
                AclTypes.aclDocumentsAccess,
                AclTypes.aclSupportTicketBacklog,
                AclTypes.aclSupportTicketView,
              ]),
              icon: <SupportIcon />,
              subItems: null,
              matchingPaths: [],
            },
            {
              label: t("common:externalLinks"),
              path: "",
              condition:
                UserHasAcls([AclTypes.aclSuperUser, AclTypes.aclExternalLinksView]) &&
                externalLinks.filter((x) => x.link && x.name).length > 0,
              icon: <LinkOutlinedIcon />,
              subItems: externalLinks
                .filter((x) => x.name && x.link)
                .map((x) => ({
                  label: x.name!,
                  path: x.link!,
                  condition: true,
                  matchingPaths: [],
                  newTab: true,
                })),
              matchingPaths: [],
            },
          ],
        },
        {
          divider: true,
          items: [
            {
              label: t("common:settings"),
              matchingPaths: [],
              path: "/settings",
              icon: <SettingsOutlinedIcon />,
              condition: true,
              subItems: generatedSettingsSubItems,
            },
          ],
        },
      ];

  return (
    <React.Fragment>
      {menuArray
        .filter((x) => x.items.some((i) => i.condition))
        .map((x, index) => (
          <React.Fragment key={`lmf${index}`}>
            {x.items.filter((i) => i.condition && (i.subItems === null || i.subItems.some((s) => s.condition))).length >
              0 && (
              <Stack key={`stack-${index}`} sx={getStackSxProps(x.divider)}>
                {x.items
                  .filter((i) => i.condition && (i.subItems === null || i.subItems.some((s) => s.condition)))
                  .map((i, indexItem) =>
                    i.subItems === null ? (
                      <LeftMenuItem item={i} open={open} key={`lm${indexItem}`} />
                    ) : (
                      <LeftMenuItemGroup item={i} open={open} key={`lmg${indexItem}`} />
                    )
                  )}
              </Stack>
            )}
          </React.Fragment>
        ))}
    </React.Fragment>
  );
};

type LeftMenuItemProps = {
  item: MenuItemInfos;
  open: boolean;
};

const LeftMenuItem: React.FC<LeftMenuItemProps> = ({ open, item }) => {
  const { pathname } = useLocation();
  const selected = isMatching(pathname, [item.path, ...item.matchingPaths]) && (item.path !== "/" || pathname === "/");
  return (
    <Tooltip disableHoverListener={open} placement="right" title={item.label}>
      <ListItemButton selected={selected} sx={getButtonSxProps(open)} component={Link} to={item.path}>
        <ListItemIcon sx={{ mr: open ? "8px" : "0px !important" }}>{item.icon}</ListItemIcon>
        {_ListItemText(item.label, open, selected)}
      </ListItemButton>
    </Tooltip>
  );
};

const LeftMenuItemGroup: React.FC<LeftMenuItemProps> = ({ open, item }) => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  //const expand = useAppSelector((state) => GetSectionExpanded(state, item.label));
  const expandSections = useSelector(GetSectionExpanded);
  const expand = useMemo(() => expandSections.includes(item.label), [expandSections, item.label]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const menuOpen = Boolean(anchorEl);
  const selected = item.path !== "" && isMatching(pathname, [item.path, ...item.matchingPaths]) && !open;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  if (!item.subItems) return <></>;
  if (!(item.subItems.length > 0)) return <></>;

  return (
    <Box
      sx={{
        borderRadius: "6px",
        mr: open ? "12px" : 0,
        ml: open ? "12px" : 0,
        width: open ? "calc(100% - 24px)" : "32px",
        position: "relative",
        transition: "background 0.5s ease",
        ":hover": {
          background: expand ? "#c2c2d61a" : "inherit",
        },
      }}
    >
      <Tooltip disableHoverListener={open} placement="right" title={item.label}>
        <ListItemButton
          id="tutorial-uikit-demo-external-links"
          sx={{
            pl: open ? "8px !important" : "6px",
            mr: 0,
            ml: 0,
            width: open ? "100%" : "32px",
            height: "32px",
            display: "flex !important",
            justifyContent: "center !important",
          }}
          component={Button}
          selected={selected}
          aria-controls={!open && expand ? "demo-positioned-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={!open && expand ? "true" : undefined}
          onClick={(e) => (open ? dispatch(ToggleSection(item.label)) : handleClick(e))}
        >
          <ListItemIcon sx={{ mr: open ? "8px" : "0px !important" }}>{item.icon}</ListItemIcon>
          {_ListItemText(item.label, open, selected)}
          {open &&
            (expand ? (
              <ArrowDropUpIcon sx={{ position: "absolute", right: "8px", top: "6px" }} />
            ) : (
              <ArrowDropDownIcon sx={{ position: "absolute", right: "8px", top: "6px" }} />
            ))}
        </ListItemButton>
      </Tooltip>
      {open ? (
        <Collapse in={expand}>
          <Stack
            sx={{
              paddingTop: 0.5,
              paddingBottom: 0.5,
              marginLeft: 1.5,
              alignContent: "center",
              alignItems: "center",
              gap: "2px",
              width: "calc(100% - 24px)",
            }}
            pt={"0px !important"}
          >
            {item.subItems
              .filter((x) => x.condition && x.path !== "")
              .map((x, i) => (
                <ListItemButton
                  key={i}
                  id="tutorial-uikit-demo-reporting"
                  sx={getButtonSxProps(open)}
                  component={Link}
                  to={x.path}
                  target={x.newTab ? "_blank" : "_self"}
                >
                  {_ListItemText(x.label, open, !isMatching(pathname, [x.path, ...x.matchingPaths]))}
                </ListItemButton>
              ))}
          </Stack>
        </Collapse>
      ) : (
        <Menu
          id="demo-positioned-menu"
          aria-labelledby="demo-positioned-button"
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "center",
            horizontal: "left",
          }}
        >
          {item.subItems
            .filter((x) => x.condition && x.path !== "")
            .map((x, i) => (
              <MenuItem sx={{ padding: 0 }} key={i}>
                <Link
                  style={{
                    width: "100%",
                    height: "100%",
                    paddingLeft: "16px",
                    paddingRight: "16px",
                    paddingTop: "6px",
                    paddingBottom: "6px",
                  }}
                  to={x.path}
                  target={x.newTab ? "_blank" : "_self"}
                >
                  {x.label}
                </Link>
              </MenuItem>
            ))}
        </Menu>
      )}
    </Box>
  );
};

const _ListItemText = (
  label: string,
  open: boolean,
  selected: boolean,
  variant?: TypographyTypeMap["props"]["variant"]
) =>
  open && (
    <ListItemText
      primary={label}
      primaryTypographyProps={{ variant: variant ?? "button" }}
      sx={{ opacity: open ? 1 : 0, margin: 0, color: selected ? "inherit" : "text.secondary", fontWeight: 500 }}
    />
  );

export default LeftMenu;
